import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield, CheckCircle2, Circle, Building2, MessageSquare, Phone, Activity,
  ChevronRight, ChevronLeft, Loader2, AlertCircle, Sparkles,
} from 'lucide-react';
import { apiGet, apiPut, apiPost } from '../lib/api.js';
import { useA2pBypass } from '../lib/a2pBypassFlag.js';
import A2PBypassBanner from '../components/A2PBypassBanner.jsx';

// TODO(settings-rebuild): wire the per-tenant A2P policy chooser to PUT
// /api/a2p/policy. When the admin selects "Standard", show companyType
// (public|private|non_profit|government) plus stockExchange and stockTicker
// inputs (only when companyType=public). Backend already validates and
// surfaces the right Twilio policy SID — this UI just needs to collect the
// fields and POST them before /a2p/brand/submit.

const ENTITY_TYPES = ['PRIVATE_PROFIT', 'PUBLIC_PROFIT', 'NON_PROFIT', 'SOLE_PROPRIETOR', 'GOVERNMENT'];
const INDUSTRIES = ['INSURANCE', 'FINANCIAL', 'REAL_ESTATE', 'PROFESSIONAL', 'HEALTHCARE', 'OTHER'];
const USE_CASES = [
  { id: 'CUSTOMER_CARE', label: 'Customer Care', desc: 'Account info, support, follow-ups' },
  { id: 'MARKETING', label: 'Marketing', desc: 'Promotional offers, campaigns' },
  { id: 'MIXED', label: 'Mixed (Care + Marketing)', desc: 'Most common for insurance agencies' },
  { id: 'HIGHER_EDUCATION', label: 'Higher Education', desc: 'Schools, universities' },
  { id: 'POLLING_VOTING', label: 'Polling / Voting', desc: 'Surveys, civic engagement' },
];

const DEFAULT_BRAND = {
  legalBusinessName: '',
  ein: '',
  entityType: 'PRIVATE_PROFIT',
  industry: 'INSURANCE',
  website: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  contactFirstName: '',
  contactLastName: '',
  contactTitle: '',
};

const DEFAULT_CAMPAIGN = {
  useCase: 'MIXED',
  description: 'CIVIX sends life insurance quote follow-ups, appointment confirmations, and customer service messages to consumers who have requested information about life, final expense, or mortgage protection coverage.',
  sampleMessage1: 'Hi #first_name, this is #agent_name with CIVIX. Your life insurance quote is ready — coverage starts at $#quote/mo. Reply YES to schedule.',
  sampleMessage2: 'Hi #first_name — confirming your appointment tomorrow at 2pm. Reply C to confirm or R to reschedule. Reply STOP to opt out.',
  optInKeywords: 'START, YES, UNSTOP',
  optOutKeywords: 'STOP, UNSUBSCRIBE, END, QUIT, CANCEL',
  helpKeywords: 'HELP, INFO',
  optInMessage: 'CIVIX: You are now opted in. Msg & data rates may apply. Reply HELP for help, STOP to cancel.',
  optOutMessage: 'CIVIX: You are unsubscribed and will receive no further messages.',
  helpMessage: 'CIVIX: For help, call (406) 860-4882. Reply STOP to opt out. Msg & data rates may apply.',
  embeddedLink: false,
  embeddedPhone: true,
  ageGated: false,
};

const STEPS = [
  { id: 1, label: 'Brand', icon: Building2 },
  { id: 2, label: 'Campaign', icon: MessageSquare },
  { id: 3, label: 'Numbers', icon: Phone },
  { id: 4, label: 'Status', icon: Activity },
];

function StatusPill({ status }) {
  const map = {
    PENDING: ['bg-white/10 text-white/60 border-white/20', 'Pending'],
    SUBMITTED: ['bg-civix-cyan/10 text-civix-cyan border-civix-cyan/30', 'Submitted'],
    APPROVED: ['bg-emerald-500/10 text-emerald-400 border-emerald-500/30', 'Approved'],
    REJECTED: ['bg-rose-500/10 text-rose-400 border-rose-500/30', 'Rejected'],
    REGISTERED: ['bg-emerald-500/10 text-emerald-400 border-emerald-500/30', 'Registered'],
  };
  const [cls, label] = map[status] || map.PENDING;
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>{label}</span>;
}

export default function A2PWizard() {
  const { hideUI: hideA2pUI } = useA2pBypass();
  const [step, setStep] = useState(1);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [campaign, setCampaign] = useState(DEFAULT_CAMPAIGN);
  const [registration, setRegistration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [attachedNumbers, setAttachedNumbers] = useState([]);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const confirmBtnRef = useRef(null);
  const cancelBtnRef = useRef(null);

  // Modal a11y: focus the Confirm button on open, ESC closes, Tab is trapped
  // between Cancel and Confirm so keyboard users can't drift behind the modal.
  useEffect(() => {
    if (!showChargeModal) return;
    confirmBtnRef.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowChargeModal(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const first = cancelBtnRef.current;
      const last = confirmBtnRef.current;
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showChargeModal]);

  const { data: phonePool } = useQuery({ queryKey: ['phone-pool'], queryFn: () => apiGet('/admin/phone-pool') });

  useEffect(() => {
    apiGet('/a2p').then((r) => {
      if (r) {
        setRegistration(r);
        if (r.brand) setBrand((b) => ({ ...b, ...r.brand }));
        if (r.campaign) setCampaign((c) => ({ ...c, ...r.campaign }));
        if (r.attachedNumbers) setAttachedNumbers(r.attachedNumbers);
      }
    }).catch(() => {});
  }, []);

  async function saveBrand() {
    setSaving(true);
    setMsg('');
    try {
      const r = await apiPut('/a2p/brand', brand);
      setRegistration(r);
      setMsg('Brand saved.');
    } catch (e) {
      setMsg('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function requestSubmitBrand() {
    setShowChargeModal(true);
  }

  async function confirmSubmitBrand() {
    setShowChargeModal(false);
    setSaving(true);
    setMsg('');
    try {
      await apiPut('/a2p/brand', brand);
      const r = await apiPost('/a2p/brand/submit', { confirmCharge: true });
      setRegistration(r?.registration || r);
      setMsg('Brand submitted to TCR.');
      setStep(2);
    } catch (e) {
      setMsg(e?.message || 'Submission failed.');
    } finally {
      setSaving(false);
    }
  }

  async function saveCampaign() {
    setSaving(true);
    setMsg('');
    try {
      const r = await apiPut('/a2p/campaign', campaign);
      setRegistration(r);
      setMsg('Campaign saved.');
    } catch (e) {
      setMsg('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function submitCampaign() {
    setSaving(true);
    setMsg('');
    try {
      await apiPut('/a2p/campaign', campaign);
      const r = await apiPost('/a2p/campaign/submit', {});
      setRegistration(r);
      setMsg('Campaign submitted.');
      setStep(3);
    } catch (e) {
      setMsg('Submission failed.');
    } finally {
      setSaving(false);
    }
  }

  async function attachNumber(num) {
    setSaving(true);
    try {
      const r = await apiPost('/a2p/numbers/attach', { number: num });
      setRegistration(r);
      if (r.attachedNumbers) setAttachedNumbers(r.attachedNumbers);
      setMsg(`Attached ${num}`);
    } catch (e) {
      setMsg('Attach failed.');
    } finally {
      setSaving(false);
    }
  }

  const brandStatus = registration?.brandStatus || 'PENDING';
  const campStatus = registration?.campaignStatus || 'PENDING';

  // Bypassed tenants ride the parent CIVIX Brand — no registration wizard.
  // Direct nav to /a2p shows the banner instead.
  if (hideA2pUI) {
    return (
      <div className="space-y-6 max-w-5xl">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Shield className="w-8 h-8 text-civix-purple" /> A2P 10DLC Registration
        </h1>
        <A2PBypassBanner />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="w-8 h-8 text-civix-purple" /> A2P 10DLC Registration
          </h1>
          <p className="text-white/60 mt-1">Register your brand and messaging campaign for compliant SMS at scale.</p>
        </div>
        <div className="flex gap-2">
          <StatusPill status={brandStatus} />
          <StatusPill status={campStatus} />
        </div>
      </div>

      {/* Stepper */}
      <div className="panel p-5">
        <div className="flex items-center justify-between">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.id;
            const done = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => setStep(s.id)}
                  className={`flex items-center gap-3 transition ${active ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition ${
                    done ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                      : active ? 'bg-civix-purple/20 border-civix-purple text-civix-purple shadow-glow-purple'
                        : 'bg-civix-panelLight border-civix-border text-white/50'
                  }`}>
                    {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-white/50">Step {s.id}</div>
                    <div className="text-sm font-semibold">{s.label}</div>
                  </div>
                </button>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${done ? 'bg-emerald-500/40' : 'bg-civix-border'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {msg && (
        <div className="panel px-4 py-3 flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4 text-civix-cyan" /> {msg}
        </div>
      )}

      {/* Step 1: Brand */}
      {step === 1 && (
        <div className="panel p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2"><Building2 className="w-5 h-5 text-civix-purple" /> Brand Information</h2>
            <StatusPill status={brandStatus} />
          </div>
          <p className="text-sm text-white/60">This information is registered with The Campaign Registry (TCR) to verify your business identity for SMS messaging.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Legal Business Name *</label>
              <input className="input" value={brand.legalBusinessName} onChange={(e) => setBrand({ ...brand, legalBusinessName: e.target.value })} placeholder="CIVIX Insurance Group LLC" />
            </div>
            <div>
              <label className="label">EIN (Tax ID) *</label>
              <input className="input" value={brand.ein} onChange={(e) => setBrand({ ...brand, ein: e.target.value })} placeholder="12-3456789" />
            </div>
            <div>
              <label className="label">Entity Type *</label>
              <select className="input" value={brand.entityType} onChange={(e) => setBrand({ ...brand, entityType: e.target.value })}>
                {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Industry *</label>
              <select className="input" value={brand.industry} onChange={(e) => setBrand({ ...brand, industry: e.target.value })}>
                {INDUSTRIES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Website</label>
              <input className="input" value={brand.website} onChange={(e) => setBrand({ ...brand, website: e.target.value })} placeholder="https://civix.app" />
            </div>
            <div>
              <label className="label">Business Email *</label>
              <input className="input" value={brand.email} onChange={(e) => setBrand({ ...brand, email: e.target.value })} placeholder="hello@civix.app" />
            </div>
            <div>
              <label className="label">Business Phone *</label>
              <input className="input" value={brand.phone} onChange={(e) => setBrand({ ...brand, phone: e.target.value })} placeholder="+14068604882" />
            </div>
            <div>
              <label className="label">Country</label>
              <input className="input" value={brand.country} onChange={(e) => setBrand({ ...brand, country: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Street Address *</label>
              <input className="input" value={brand.street} onChange={(e) => setBrand({ ...brand, street: e.target.value })} />
            </div>
            <div>
              <label className="label">City *</label>
              <input className="input" value={brand.city} onChange={(e) => setBrand({ ...brand, city: e.target.value })} />
            </div>
            <div>
              <label className="label">State *</label>
              <input className="input" value={brand.state} onChange={(e) => setBrand({ ...brand, state: e.target.value })} />
            </div>
            <div>
              <label className="label">Zip *</label>
              <input className="input" value={brand.zip} onChange={(e) => setBrand({ ...brand, zip: e.target.value })} />
            </div>
          </div>

          <div className="border-t border-civix-border pt-5">
            <h3 className="text-base font-semibold mb-3">Authorized Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">First Name *</label>
                <input className="input" value={brand.contactFirstName} onChange={(e) => setBrand({ ...brand, contactFirstName: e.target.value })} />
              </div>
              <div>
                <label className="label">Last Name *</label>
                <input className="input" value={brand.contactLastName} onChange={(e) => setBrand({ ...brand, contactLastName: e.target.value })} />
              </div>
              <div>
                <label className="label">Title</label>
                <input className="input" value={brand.contactTitle} onChange={(e) => setBrand({ ...brand, contactTitle: e.target.value })} placeholder="Owner / CEO" />
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <button className="btn-ghost" onClick={saveBrand} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Draft'}
            </button>
            <button className="btn-primary" onClick={requestSubmitBrand} disabled={saving || !brand.legalBusinessName || !brand.ein}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit Brand for Review <ChevronRight className="w-4 h-4 ml-1" /></>}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Campaign */}
      {step === 2 && (
        <div className="panel p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-civix-purple" /> Messaging Campaign</h2>
            <StatusPill status={campStatus} />
          </div>
          <p className="text-sm text-white/60">Define what you'll send. Carriers review this to allow throughput on registered numbers.</p>

          <div>
            <label className="label">Use Case *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {USE_CASES.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setCampaign({ ...campaign, useCase: u.id })}
                  className={`text-left p-4 rounded-xl border transition ${campaign.useCase === u.id ? 'border-civix-purple bg-civix-purple/10 shadow-glow-purple' : 'border-civix-border hover:border-civix-purple/40 bg-civix-panelLight'}`}
                >
                  <div className="font-semibold text-sm">{u.label}</div>
                  <div className="text-xs text-white/60 mt-1">{u.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Campaign Description *</label>
            <textarea className="input min-h-[100px]" value={campaign.description} onChange={(e) => setCampaign({ ...campaign, description: e.target.value })} />
            <p className="text-xs text-white/40 mt-1">Describe what messages your customers will receive and why.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Sample Message 1 *</label>
              <textarea className="input min-h-[90px]" value={campaign.sampleMessage1} onChange={(e) => setCampaign({ ...campaign, sampleMessage1: e.target.value })} />
            </div>
            <div>
              <label className="label">Sample Message 2 *</label>
              <textarea className="input min-h-[90px]" value={campaign.sampleMessage2} onChange={(e) => setCampaign({ ...campaign, sampleMessage2: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Opt-In Keywords</label>
              <input className="input" value={campaign.optInKeywords} onChange={(e) => setCampaign({ ...campaign, optInKeywords: e.target.value })} />
            </div>
            <div>
              <label className="label">Opt-Out Keywords *</label>
              <input className="input" value={campaign.optOutKeywords} onChange={(e) => setCampaign({ ...campaign, optOutKeywords: e.target.value })} />
            </div>
            <div>
              <label className="label">Help Keywords *</label>
              <input className="input" value={campaign.helpKeywords} onChange={(e) => setCampaign({ ...campaign, helpKeywords: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="label">Opt-In Confirmation Message</label>
              <input className="input" value={campaign.optInMessage} onChange={(e) => setCampaign({ ...campaign, optInMessage: e.target.value })} />
            </div>
            <div>
              <label className="label">Opt-Out Confirmation Message *</label>
              <input className="input" value={campaign.optOutMessage} onChange={(e) => setCampaign({ ...campaign, optOutMessage: e.target.value })} />
            </div>
            <div>
              <label className="label">Help Reply Message *</label>
              <input className="input" value={campaign.helpMessage} onChange={(e) => setCampaign({ ...campaign, helpMessage: e.target.value })} />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-civix-panelLight border border-civix-border cursor-pointer">
              <input type="checkbox" checked={campaign.embeddedLink} onChange={(e) => setCampaign({ ...campaign, embeddedLink: e.target.checked })} />
              <span className="text-sm">Messages contain links</span>
            </label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-civix-panelLight border border-civix-border cursor-pointer">
              <input type="checkbox" checked={campaign.embeddedPhone} onChange={(e) => setCampaign({ ...campaign, embeddedPhone: e.target.checked })} />
              <span className="text-sm">Messages contain phone numbers</span>
            </label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-civix-panelLight border border-civix-border cursor-pointer">
              <input type="checkbox" checked={campaign.ageGated} onChange={(e) => setCampaign({ ...campaign, ageGated: e.target.checked })} />
              <span className="text-sm">Age-gated content</span>
            </label>
          </div>

          <div className="flex justify-between pt-4">
            <button className="btn-ghost" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</button>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={saveCampaign} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Draft'}
              </button>
              <button className="btn-primary" onClick={submitCampaign} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit Campaign <ChevronRight className="w-4 h-4 ml-1" /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Numbers */}
      {step === 3 && (
        <div className="panel p-6 space-y-5">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Phone className="w-5 h-5 text-civix-purple" /> Attach Phone Numbers</h2>
          <p className="text-sm text-white/60">Attach numbers from your pool to this campaign. Only attached numbers can send under the registered campaign throughput.</p>

          <div className="space-y-2">
            {(phonePool?.numbers || []).map((p) => {
              const attached = attachedNumbers.includes(p.number);
              return (
                <div key={p._id} className="flex items-center justify-between p-4 rounded-xl bg-civix-panelLight border border-civix-border">
                  <div>
                    <div className="font-mono font-semibold">{p.number}</div>
                    <div className="text-xs text-white/50">{p.friendlyName} • {p.state}</div>
                  </div>
                  {attached ? (
                    <span className="inline-flex items-center gap-2 text-emerald-400 text-sm font-medium">
                      <CheckCircle2 className="w-4 h-4" /> Attached
                    </span>
                  ) : (
                    <button className="btn-ghost text-sm" onClick={() => attachNumber(p.number)} disabled={saving}>Attach</button>
                  )}
                </div>
              );
            })}
            {!(phonePool?.numbers || []).length && (
              <div className="text-center text-white/50 py-8">No phone numbers in pool. Purchase a number under Phone Setup first.</div>
            )}
          </div>

          <div className="flex justify-between pt-4">
            <button className="btn-ghost" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</button>
            <button className="btn-primary" onClick={() => setStep(4)}>View Status <ChevronRight className="w-4 h-4 ml-1" /></button>
          </div>
        </div>
      )}

      {/* Step 4: Status */}
      {step === 4 && (
        <div className="panel p-6 space-y-5">
          <h2 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5 text-civix-purple" /> Registration Status</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-civix-panelLight border border-civix-border">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-white/60">Brand</div>
                <StatusPill status={brandStatus} />
              </div>
              <div className="text-lg font-semibold">{brand.legalBusinessName || '—'}</div>
              <div className="text-xs text-white/50 mt-1">EIN: {brand.ein || '—'}</div>
              {registration?.brandId && <div className="text-xs text-white/50 mt-1">Brand ID: {registration.brandId}</div>}
              {registration?.twilio?.profileSource === 'shared_parent' && (
                <div className="text-[10px] uppercase tracking-wider mt-2 inline-block px-2 py-0.5 rounded-sm bg-civix-purple/20 text-civix-purple border border-civix-purple/40">
                  Using shared parent profile
                </div>
              )}
            </div>
            <div className="p-5 rounded-xl bg-civix-panelLight border border-civix-border">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-white/60">Campaign</div>
                <StatusPill status={campStatus} />
              </div>
              <div className="text-lg font-semibold">{campaign.useCase}</div>
              <div className="text-xs text-white/50 mt-1">{attachedNumbers.length} number(s) attached</div>
              {registration?.campaignId && <div className="text-xs text-white/50 mt-1">Campaign ID: {registration.campaignId}</div>}
            </div>
          </div>

          <div className="p-5 rounded-xl bg-civix-purple/10 border border-civix-purple/30">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-civix-purple shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold mb-1">What happens next</div>
                <ul className="text-white/70 space-y-1 text-xs">
                  <li>• Brand verification typically takes 1–3 business days</li>
                  <li>• Campaign approval typically takes 2–7 business days after brand approval</li>
                  <li>• Once approved, throughput limits are lifted on attached numbers</li>
                  <li>• Status updates come via the TCR webhook automatically</li>
                </ul>
              </div>
            </div>
          </div>

          {registration?.events?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Audit Trail</h3>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {registration.events.slice().reverse().map((e, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs p-2 rounded-sm bg-civix-panelLight border border-civix-border/50">
                    <Circle className="w-2 h-2 fill-civix-cyan text-civix-cyan" />
                    <span className="text-white/80 font-medium">{e.type}</span>
                    <span className="text-white/40">{e.message}</span>
                    <span className="text-white/40 ml-auto">{new Date(e.at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-start pt-4">
            <button className="btn-ghost" onClick={() => setStep(3)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</button>
          </div>
        </div>
      )}

      {showChargeModal && (
        <div
          data-testid="a2p-charge-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(10, 10, 15, 0.78)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="a2p-charge-title"
        >
          <div
            className="max-w-md w-full rounded-2xl p-6 border"
            style={{
              background: '#0A0A0F',
              borderColor: 'rgba(124, 58, 237, 0.45)',
              boxShadow: '0 0 36px rgba(124, 58, 237, 0.35), 0 0 18px rgba(224, 64, 251, 0.18)',
            }}
          >
            <h3 id="a2p-charge-title" className="text-lg font-semibold mb-3" style={{ color: '#E040FB' }}>
              Confirm brand submission
            </h3>
            <p className="text-sm mb-2" style={{ color: '#fff' }}>
              Twilio will charge your account immediately:
            </p>
            <ul className="text-sm space-y-1 mb-4" style={{ color: 'rgba(255,255,255,0.85)' }}>
              <li>• Standard brand: <strong>~$44</strong> (most agencies)</li>
              <li>• Sole-Proprietor brand: <strong>~$4</strong></li>
            </ul>
            <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>
              This charge cannot be undone. Verify your business details are correct before continuing.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                ref={cancelBtnRef}
                onClick={() => setShowChargeModal(false)}
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.18)' }}
              >
                Cancel
              </button>
              <button
                type="button"
                ref={confirmBtnRef}
                data-testid="a2p-charge-confirm"
                onClick={confirmSubmitBrand}
                disabled={saving}
                aria-busy={saving}
                className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #7C3AED 0%, #E040FB 60%, #3B82F6 100%)',
                  color: '#fff',
                  boxShadow: '0 0 18px rgba(124, 58, 237, 0.45)',
                }}
              >
                {saving ? 'Submitting…' : 'Confirm & pay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
